// Authenticated layout: module rail, feature sidebar, header (main column), body
import React from 'react';
import { Input, Modal, Select } from 'antd';

import { NotificationsStreamManager } from '@/components/notifications/NotificationsStreamManager';
import { InsideBody } from '@/components/layout/InsideBody';
import { InsideSingleSidebar } from '@/components/layout/InsideSingleSidebar';
import { InsideFeatureSidebar } from '@/components/layout/InsideFeatureSidebar';
import { InsideHeader } from '@/components/layout/InsideHeader';
import { InsideRoot, InsideShellRow, MainColumn, MODULE_RAIL_WIDTH, HeaderChrome } from '@/components/layout/insideShell.styles';
import { useInsideLayout } from '@hooks/layout/useInsideLayout';
import { theme } from '@theme/theme';

import { MobileSidebarDrawer } from './MobileSidebarDrawer';
import CreateBugReportModal from '@/components/bug-report/CreateBugReportModal';

const Inside = ({ children }) => {
    const L = useInsideLayout();

    return (
        <InsideRoot>
            <NotificationsStreamManager />
            <InsideShellRow>
                {!L.isMobile && <InsideSingleSidebar L={L} />}
                {!L.isMobile && L.moduleId === 'chat' && <InsideFeatureSidebar L={L} />}

                <MainColumn style={L.moduleId === 'chat' ? { background: 'transparent' } : {}}>
                    <HeaderChrome>
                        <InsideHeader
                            isMobile={L.isMobile}
                            onOpenMobileDrawer={() => L.setDrawerOpen(true)}
                            breadcrumbItems={L.breadcrumbItems}
                            navigate={L.navigate}
                            workspaceMenuItems={L.workspaceMenuItems}
                            handleWorkspaceMenuClick={L.handleWorkspaceMenuClick}
                            effectiveWorkspace={L.effectiveWorkspace}
                        />
                    </HeaderChrome>

                    <InsideBody L={L}>{children}</InsideBody>
                </MainColumn>
            </InsideShellRow>

            <Modal
                title={L.createChatType === 'group' ? 'Create group' : 'New direct message'}
                open={L.createChatOpen}
                onCancel={L.closeCreateChat}
                onOk={L.submitCreateChat}
                okText={L.createChatType === 'group' ? 'Create group' : 'Chat'}
                cancelText="Cancel"
                maskClosable={false}
                confirmLoading={L.createChatMutation.isPending}
            >
                <div style={{ marginBottom: 8, color: 'var(--ant-color-text-secondary)', fontSize: 13 }}>
                    {L.createChatType === 'group' ? 'Select 2 or more users.' : 'Select 1 user.'}
                </div>
                {L.createChatType === 'group' && (
                    <div style={{ marginBottom: 12 }}>
                        <Input
                            value={L.createChatGroupName}
                            onChange={(e) => L.setCreateChatGroupName(e.target.value)}
                            placeholder="Group name"
                        />
                    </div>
                )}
                <Select
                    mode={L.createChatType === 'group' ? 'multiple' : undefined}
                    value={L.createChatSelectedUserIds}
                    onChange={(val) =>
                        L.setCreateChatSelectedUserIds(Array.isArray(val) ? val : val ? [val] : [])
                    }
                    placeholder="Select users"
                    style={{ width: '100%' }}
                    loading={L.eligibleUsersLoading}
                    showSearch
                    optionFilterProp="label"
                    options={L.eligibleUserOptions}
                />
            </Modal>

            {L.isMobile && (
                <MobileSidebarDrawer
                    isOpen={L.drawerOpen}
                    onClose={() => L.setDrawerOpen(false)}
                    mode={L.mode}
                    moduleBarWidth={MODULE_RAIL_WIDTH}
                    currentUserAvatarColor={L.currentUserAvatarColor}
                    currentUserAvatarUrl={L.currentUserAvatarUrl}
                    getUserInitials={L.getUserInitials}
                    getUserName={L.getUserName}
                    getUserRole={L.getUserRole}
                    userDropdownOpen={L.userDropdownOpen}
                    setUserDropdownOpen={L.setUserDropdownOpen}
                    userMenuItems={L.userMenuItems}
                    handleUserMenuClick={L.handleUserMenuClick}
                    primaryModuleSelectedKeys={L.primaryModuleSelectedKeys}
                    primaryModuleMenuItems={L.primaryModuleMenuItems}
                    moreModuleItems={L.moreModuleItems}
                    moreModulesOpen={L.moreModulesOpen}
                    setMoreModulesOpen={L.setMoreModulesOpen}
                    isMoreModuleActive={L.isMoreModuleActive}
                    moduleId={L.moduleId}
                    handleMenuClick={L.handleMenuClick}
                    projectIdFromPath={L.projectIdFromPath}
                    projectDetailIsError={L.projectDetailIsError}
                    projectHeaderDisplayName={L.projectHeaderDisplayName}
                    featureSidebarTitle={L.featureSidebarTitle}
                    selectedKeys={L.selectedKeys}
                    featureSidebarItems={L.featureSidebarItems}
                    featureSidebarSelectedKeys={L.featureSidebarSelectedKeys}
                    projectSidebarDisplayItems={L.projectSidebarDisplayItems}
                />
            )}

            <Modal
                maskClosable={false}
                title="Log out?"
                open={L.logoutModalOpen}
                onCancel={() => L.setLogoutModalOpen(false)}
                onOk={L.handleLogoutConfirm}
                okText="Log out"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                width={theme.modalSizes.sm}
            >
                Are you sure you want to log out?
            </Modal>

            <CreateBugReportModal
                open={L.createBugModalOpen}
                onCancel={() => L.setCreateBugModalOpen(false)}
            />
        </InsideRoot>
    );
};

export default Inside;
